/*
 * CustomerOrderForm.tsx : Un formulaire pour ajouter ou modifier une commande client.
 */

// Import React
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

// Import FontAwesome
import {
	faBox,
	faClock,
	faInfoCircle,
} from '@fortawesome/free-solid-svg-icons';

// Import Communs et Layout
import {
	DatePickerField,
	finishFormSave,
	FormSaveActions,
	InputField,
	SelectField,
	StatusCard,
	useFormDirty,
} from '../../../common';
import { useWorkflowStatusConfig } from '../../../common/hooks/useWorkflowStatusConfig';
import CardHeader from '../../../layout/CardHeader';
import CardSection from '../../../layout/CardSection';

// Import CustomerOrder
import {
	CUSTOMER_ORDER_STATUS_CONFIG,
	CustomerOrderData,
} from '../types/types';
import { customerOrdersApi } from '../services/customerOrdersApi';
import { projectsApi } from 'features/projects/services/projectsApi';
import { quotesApi } from 'features/quotes/services/quotesApi';

function editableOrder(order: CustomerOrderData) {
	const editable = { ...order };
	delete editable.status;
	return editable;
}

// Composant CustomerOrderForm
const CustomerOrderForm: React.FC = () => {
	// Hooks de State
	const [loading, setLoading] = React.useState(true);
	const [baselineReady, setBaselineReady] = React.useState(false);
	const userId = localStorage.getItem('userId');
	const userName = localStorage.getItem('name');
	const userFirstName = localStorage.getItem('firstName');
	const [formData, setFormData] = React.useState<CustomerOrderData>({
		id: 0,
		referenceNumber: '',
		orderDate: new Date(),
		amount: 0,
		status: 'DRAFT',
		project: null,
		quote: null,
		createdBy: userId
			? {
					id: Number(userId),
					name: userName || '',
					firstName: userFirstName || '',
				}
			: undefined,
		createdAt: new Date()
			.toISOString()
			.replace('Z', '+00:00') as unknown as Date,
	});

	// Hooks select project
	const [projectOptions, setProjectOptions] = React.useState<
		{ key: string; label: string }[]
	>([]);
	const [loadingProjects, setLoadingProjects] = React.useState(true);
	const [quoteOptions, setQuoteOptions] = React.useState<
		{ key: string; label: string }[]
	>([]);
	const [loadingQuotes, setLoadingQuotes] = React.useState(true);
	const statusConfig = useWorkflowStatusConfig(
		'customer_order',
		CUSTOMER_ORDER_STATUS_CONFIG
	);

	// Hook de Navigation et Paramètres de navigation
	const navigate = useNavigate();
	const { id } = useParams<{ id: string }>();
	// L'état est enregistré dès le clic sur le bloc État.
	const { isDirty, markClean } = useFormDirty(
		editableOrder(formData),
		baselineReady
	);

	// Une fois le composant monté et si un id est présent, fetch des données et préremplissage du formulaire
	React.useEffect(() => {
		// Vérifier la présence du token d'authentification
		const token = localStorage.getItem('token');
		if (!token) return;

		if (id) {
			customerOrdersApi
				.get(Number(id), token)
				.then((data) => {
					setFormData(data);
					// Préremplir les selects
					if (data.project) {
						setProjectOptions([
							{
								key: data.project.id.toString(),
								label: data.project.referenceNumber,
							},
						]);
					}
					if (data.quote) {
						setQuoteOptions([
							{
								key: data.quote.id.toString(),
								label: data.quote.referenceNumber,
							},
						]);
					}
				})
				.catch((error) => console.error(error))
				.finally(() => {
					setLoading(false);
					setBaselineReady(true);
				});
		} else {
			// Si pas d'id, on arrête le loading directement
			setLoading(false);
			setBaselineReady(true);
		}
	}, [id]);

	// Lazy load des options projets en arrière-plan (route /select allégée)
	React.useEffect(() => {
		const token = localStorage.getItem('token');
		if (!token) return;

		if (!loading && loadingProjects) {
			projectsApi
				.listSelect(token)
				.then((items) => {
					setProjectOptions(
						items.map((item) => ({
							key: item.id.toString(),
							label: String(item.referenceNumber),
						}))
					);
				})
				.catch(console.error)
				.finally(() => setLoadingProjects(false));
		}
	}, [loading, loadingProjects]);

	React.useEffect(() => {
		const token = localStorage.getItem('token');
		if (!token || loading || !loadingQuotes) return;

		quotesApi
			.listSelect(token)
			.then((items) => {
				setQuoteOptions(
					items.map((item) => ({
						key: item.id.toString(),
						label: String(item.referenceNumber),
					}))
				);
			})
			.catch(console.error)
			.finally(() => setLoadingQuotes(false));
	}, [loading, loadingQuotes]);

	// Gestion de la soumission du formulaire
	const [isSubmitting, setIsSubmitting] = React.useState(false);
	const handleSave = (shouldLeave: boolean) => {
		// Vérifier la présence du token d'authentification
		const token = localStorage.getItem('token');
		if (!token) return;
		// Indiquer que la soumission est en cours
		setIsSubmitting(true);
		const onLeave = () => navigate('/app/customerOrders');
		// Si un id est présent, mise à jour de l'élément existant
		if (id) {
			customerOrdersApi
				.update(Number(id), formData, token)
				.then(() => finishFormSave(shouldLeave, { onLeave, markClean }))
				.catch((error) => console.error(error))
				.finally(() => setIsSubmitting(false));
		} else {
			// Sinon, création d'un nouvel élément
			customerOrdersApi
				.create(formData, token)
				.then((created) =>
					finishFormSave(shouldLeave, {
						onLeave,
						markClean,
						stayOnEdit:
							!id && created?.id
								? () =>
										navigate(`/app/customerOrders/${created.id}/edit`, {
											replace: true,
										})
								: undefined,
					})
				)
				.catch((error) => console.error(error))
				.finally(() => setIsSubmitting(false));
		}
	};

	const handleStatusChange = (newStatus: string) => {
		if (!formData.id) return;
		const token = localStorage.getItem('token');
		if (!token) return;
		customerOrdersApi
			.update(
				Number(formData.id),
				{
					status: newStatus,
					quote: formData.quote,
					project: formData.project,
				},
				token
			)
			.then((updated) => {
				setFormData((prev) => ({
					...prev,
					status: updated?.status ?? newStatus,
					project: updated?.project ?? prev.project,
					quote: updated?.quote ?? prev.quote,
				}));
			})
			.catch((error) => console.error(error));
	};

	const handleSubmit = (e: React.FormEvent) => {
		// Empêcher le comportement par défaut du formulaire
		e.preventDefault();
		handleSave(true);
	};

	// Gestion des changements dans le formulaire pour les selects
	const handleChangeSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const { name, value } = e.target;

		if (name === 'project') {
			const selectedOption = projectOptions.find((opt) => opt.key === value);
			if (selectedOption) {
				setFormData((prev) => ({
					...prev,
					project: {
						id: Number(selectedOption.key),
						referenceNumber: selectedOption.label,
					},
				}));
			}
			return;
		}

		if (name === 'quote') {
			const selectedOption = quoteOptions.find((opt) => opt.key === value);
			if (selectedOption) {
				setFormData((prev) => ({
					...prev,
					quote: {
						id: Number(selectedOption.key),
						referenceNumber: selectedOption.label,
					},
				}));
			}
			return;
		}

		// Mise à jour standard pour les autres champs
		setFormData((prev) => ({ ...prev, [name]: value }));
	};

	// Rendu du composant, le premier div est la card principale
	return (
		<div className="max-w-6xl mx-auto p-6 bg-white rounded-lg shadow-md space-y-6">
			{/* HEADER DE LA CARD */}
			<CardHeader
				icon={faBox}
				title={
					id ? "Édition d'une commande client" : 'Nouvelle commande client'
				}
				createdAt={
					formData.createdAt ? formData.createdAt?.toLocaleString() : undefined
				}
				createdBy={
					formData.createdBy
						? formData.createdBy.firstName + ' ' + formData.createdBy.name
						: undefined
				}
				loading={loading}
			/>

			{/* CONTENU DE LA CARD */}
			<form onSubmit={handleSubmit} className="space-y-6">
				{/* 3 COLONNES */}
				<div className="grid grid-cols-1 md:grid-cols-3 gap-6">
					{/* COLONNE GAUCHE */}
					<CardSection icon={faInfoCircle} title="Informations générales">
						<SelectField
							label="Devis"
							selectedKey={formData.quote?.id?.toString() || ''}
							onValueChange={(val) =>
								handleChangeSelect({
									target: {
										name: 'quote',
										value: val,
									},
								} as React.ChangeEvent<HTMLSelectElement>)
							}
							options={quoteOptions}
							loading={loading}
							optionsLoading={loadingQuotes}
							entityHref={
								formData.quote?.id
									? `/app/quotes/${formData.quote.id}/manage`
									: undefined
							}
							onClear={() =>
								setFormData((prev) => ({
									...prev,
									quote: null,
								}))
							}
						/>
						<SelectField
							label="Affaire"
							selectedKey={formData.project?.id?.toString() || ''}
							onValueChange={(val) =>
								handleChangeSelect({
									target: {
										name: 'project',
										value: val,
									},
								} as React.ChangeEvent<HTMLSelectElement>)
							}
							options={projectOptions}
							loading={loading}
							optionsLoading={loadingProjects}
							entityHref={
								formData.project?.id
									? `/app/projects/${formData.project.id}/edit`
									: undefined
							}
							onClear={() =>
								setFormData((prev) => ({
									...prev,
									project: null,
								}))
							}
						/>
						<InputField
							label="Numéro de référence"
							name="referenceNumber"
							value={formData.referenceNumber}
							onChange={(e) =>
								setFormData({ ...formData, referenceNumber: e.target.value })
							}
							loading={loading}
							placeholder="Celui fourni par le client"
						/>
						<InputField
							label="Montant"
							name="amount"
							type="number"
							value={formData.amount?.toString() || ''}
							onChange={(e) =>
								setFormData({ ...formData, amount: parseFloat(e.target.value) })
							}
							loading={loading}
						/>
					</CardSection>

					{/* COLONNE CENTRE */}
					<CardSection icon={faClock} title="Dates et périodicité">
						<DatePickerField
							label="Date de la commande"
							name="orderDate"
							value={
								formData.orderDate ? new Date(formData.orderDate) : undefined
							}
							onChange={(date) =>
								setFormData({ ...formData, orderDate: date || undefined })
							}
							loading={loading}
						/>
					</CardSection>

					{/* COLONNE DROITE */}
					{id && (
						<StatusCard
							status={formData.status}
							config={statusConfig}
							onStatusChange={handleStatusChange}
							size="md"
							loading={loading}
						/>
					)}
				</div>
			</form>

			<FormSaveActions
				isDirty={isDirty}
				isSubmitting={isSubmitting}
				onCancel={() => navigate('/app/customerOrders')}
				onSave={() => handleSave(false)}
				onSaveAndLeave={() => handleSave(true)}
			/>
		</div>
	);
};

export default CustomerOrderForm;
